import React from "react";
import { Paper, Grid, TextField, MenuItem, Button } from "@material-ui/core";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "app/store";
import { setSalesFilters } from "app/main/sales/store/filter-sales.slice";
import { fetchSales } from "app/main/sales/store/sales-list.slice";
import { PartnerProvider, SalesSource } from "app/types/service-types/SalesTypes";
import { useTranslation } from "react-i18next";
import { SalesService } from "app/services/SalesServices";
const SOURCES: (SalesSource | "")[] = [
  "",
  "ADMIN",
  "WEB",
  "ANDROID",
  "IOS",
  "SKYSCANNER",
  "SKYSCANNER_MOB",
  "WEB_MANUAL",
  "BMT_NETWORK",
  "BMT_NETWORK_EDREAMS",
  "BMT_NETWORK_BOOKING",
];
const PROVIDERS: (PartnerProvider | "")[] = ["", "LOCAL", "FLYTIK", "RACI"];
const boolOpts = [
  { label: "true", value: true },
  { label: "false", value: false },
];

const SearchSaleTable: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((s: AppState) => s.sales.filters);

  const submit = () => dispatch(fetchSales(filters));

  const download = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPdf = async () => {
    const resp = await SalesService.exportPdf(filters);

    const filename =
      resp.headers["content-disposition"]
        ?.split("filename=")?.[1]
        ?.replace(/"/g, "") ||
      `sales_${filters.fromDate}_${filters.toDate}.pdf`;

    const blob = new Blob([resp.data], { type: "application/pdf" });
    download(blob, filename);
  };

  const onXls = async () => {
    const resp = await SalesService.exportXls(filters);
    const filename =
      resp.headers["content-disposition"]
        ?.split("filename=")?.[1]
        ?.replace(/"/g, "") ||
      `sales_${filters.fromDate}_${filters.toDate}.xls`;
    download(resp.data, filename);
  };

  return (
    <Paper style={{ padding: 12, marginBottom: 12 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={2}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              inputVariant="standard"
              disableToolbar
              autoOk
              variant="inline"
              format="dd/MM/yyyy"
            label={t("From date", "From date")}
            name="fromDate"
            value={filters.fromDate ? new Date(filters.fromDate) : null}
            style={{ width: "100%" }}
            onChange={(date: Date | null) => {
              const newFromDate = date ? date.toISOString().slice(0, 10) : "";

              const nextFilters: any = {
                fromDate: newFromDate,
              };

              if (filters.toDate && date && new Date(filters.toDate) < date) {
                nextFilters.toDate = "";
              }

              dispatch(setSalesFilters(nextFilters));
            }}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xs={12} sm={2}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              inputVariant="standard"
              disableToolbar
              autoOk
              variant="inline"
              format="dd/MM/yyyy"
            label={t("To date", "To date")}
            name="toDate"
            value={filters.toDate ? new Date(filters.toDate) : null}
            style={{ width: "100%", border: "0 0 2px 0" }}
            minDate={filters.fromDate ? new Date(filters.fromDate) : undefined}
            onChange={(date: Date | null) =>
              dispatch(
                setSalesFilters({
                  toDate: date ? date.toISOString().slice(0, 10) : "",
                }),
              )
            }
            />
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            select
            fullWidth
            label={t("Paid", "Paid")}
            value={String(filters.isPaid)}
            onChange={(e) =>
              dispatch(setSalesFilters({ isPaid: e.target.value === "true" }))
            }
          >
            {boolOpts.map((b) => (
              <MenuItem key={String(b.value)} value={String(b.value)}>
                {b.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            select
            fullWidth
            label={t("Source", "Source")}
            value={filters.source ?? ""}
            onChange={(e) =>
              dispatch(
                setSalesFilters({
                  source: e.target.value as SalesSource | "",
                }),
              )
            }
          >
            {SOURCES.map((s) => (
              <MenuItem key={s || "all"} value={s}>
                {s || t("All", "All")}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            select
            fullWidth
            label={t("Provider", "Provider")}
            value={filters.provider ?? ""}
            onChange={(e) => dispatch(setSalesFilters({ provider: e.target.value as PartnerProvider | "" }))}
          >
            {PROVIDERS.map((provider) => (
              <MenuItem key={provider || "all"} value={provider}>
                {provider || t("All", "All")}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={2}>
          <Button variant="outlined" onClick={submit} fullWidth>
            {t("Search", "Search")}
          </Button>
        </Grid>

        <Grid item xs={12} sm={"auto"}>
          <Button variant="outlined" onClick={onPdf}>
            {t("Export PDF", "Export PDF")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={"auto"}>
          <Button variant="outlined" onClick={onXls}>
            {t("Export Excel", "Export Excel")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchSaleTable;
